import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { SnackbarKey, SnackbarProvider } from 'notistack';
import { createRef, useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { useLocation } from 'react-router-dom';
import AppLoadingProgress from './AppLoadingProgress';
import { ConfigProvider } from './config';
import ErrorHandler from './ErrorHandler';
import routes, { AppRoutes, useNavigate, useQuery } from './routes';
import store from './store';
import { SwitchThemeProvider } from './theme';
import { en } from './translations';
import { LaunchDarklyProvider } from './launchdarkly';
import { NotificationProvider } from './notification';
import { AccountProvider } from './account';
import { alias, AnalyticsProvider, normalizeAnonymousId } from './analytics';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    width: '100vw',
    height: '100vh',
  },
  snackbar: {
    paddingLeft: theme.spacing(2),
  },
  iconSuccess: {
    color: '#36B37E',
    marginRight: theme.spacing(1),
  },
  iconError: {
    color: '#FA6F50',
    marginRight: theme.spacing(1),
  },
  // see why !important is used here: https://github.com/iamhosseindhv/notistack/issues/461
  variantSuccess: {
    backgroundColor: '#E7F8F1 !important',
    color: '#000000 !important',
  },
  variantError: {
    backgroundColor: '#FFEDE6 !important',
    color: '#000000 !important',
  },
  action: {
    marginLeft: theme.spacing(2),
  },
  closeButton: {
    padding: 0,
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  closeIcon: {
    color: '#516393',
    backgroundColor: 'inherit',
  },
  // end error region
}));

const ThemeProviderChildrenAdapter = () => {
  const classes = useStyles();
  const notistackRef = createRef<SnackbarProvider>();
  const onClickDismiss = (key: SnackbarKey) => () => {
    if (!notistackRef.current) return;
    notistackRef.current.closeSnackbar(key);
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <IntlProvider locale="en" messages={en}>
        <NotificationProvider>
          <SnackbarProvider
            className={classes.snackbar}
            hideIconVariant={false}
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            iconVariant={{
              success: <CheckCircleIcon className={classes.iconSuccess} />,
              error: <ErrorIcon className={classes.iconError} />,
            }}
            classes={{
              variantSuccess: classes.variantSuccess,
              variantError: classes.variantError,
            }}
            ref={notistackRef}
            action={key => (
              <Box className={classes.action}>
                <IconButton
                  classes={{ root: classes.closeButton }}
                  onClick={onClickDismiss(key)}
                  size="large"
                >
                  <HighlightOffIcon className={classes.closeIcon} fontSize="small" />
                </IconButton>
              </Box>
            )}
          >
            {
              // This component must be mounted here
              // because it needs to inject Store, intl, Snackbar as dependencies
            }
            <ErrorHandler />
            <AppLoadingProgress />
            <AccountProvider>
              <AppRoutes routes={routes} />
            </AccountProvider>
          </SnackbarProvider>
        </NotificationProvider>
      </IntlProvider>
    </div>
  );
};

const App = () => {
  const location = useLocation();
  const { replace, replaceQuery } = useNavigate();
  const { clear, get, queryDictionary } = useQuery();

  useEffect(() => {
    const anonymousIdFromQuery = get('anonymous_id');
    if (anonymousIdFromQuery) {
      // The sender may forward the raw JSON-encoded `ajs_anonymous_id` (wrapped in quotes).
      const incomingAnonymousId = normalizeAnonymousId(anonymousIdFromQuery);
      if (incomingAnonymousId) {
        alias(incomingAnonymousId);
      }
      clear('anonymous_id');
      replace(undefined, replaceQuery(queryDictionary()));
    }
  }, [replace, location.search]);

  return (
    <ConfigProvider>
      <LaunchDarklyProvider>
        <Provider store={store}>
          <AnalyticsProvider>
            <SwitchThemeProvider>
              <ThemeProviderChildrenAdapter />
            </SwitchThemeProvider>
          </AnalyticsProvider>
        </Provider>
      </LaunchDarklyProvider>
    </ConfigProvider>
  );
};

export default App;
